// contexts/ConfirmContext.jsx
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import ConfirmAction from "../modals/ConfirmAction";

const ConfirmContext = createContext(null);

const defaultOptions = {
  title: "Are you sure?",
  bodyText: "",
  submitButtonText: "Confirm",
  submitButtonColor: "primary", // MUI Button color
};

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(defaultOptions);
  const resolveRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setOptions({ ...defaultOptions, ...opts });
    setOpen(true);

    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  const handleSubmit = useCallback(async () => {
    // If caller passed onConfirm, run it here
    if (options.onConfirm) {
      await options.onConfirm();
    }
    setOpen(false);
    resolveRef.current?.(true);
    resolveRef.current = null;
  }, [options]);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <ConfirmAction
        open={open}
        title={options.title}
        bodyText={options.bodyText}
        submitButtonText={options.submitButtonText}
        submitButtonColor={options.submitButtonColor}
        handleSubmit={handleSubmit}
        onClose={handleClose}
      />
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return ctx;
}
